import { useEffect } from "react";
import { useLocation } from "react-router";
import { canonicalUrl, findRoute } from "@/routes";

function upsertMeta(
  selector: string,
  attribute: "name" | "property",
  key: string,
  content: string,
) {
  let element = document.head.querySelector<HTMLMetaElement>(selector);

  if (!element) {
    element = document.createElement("meta");
    element.setAttribute(attribute, key);
    document.head.append(element);
  }

  element.setAttribute("content", content);
}

function upsertCanonical(href: string) {
  let element = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');

  if (!element) {
    element = document.createElement("link");
    element.rel = "canonical";
    document.head.append(element);
  }

  element.href = href;
}

function upsertSchema(path: string, schema: unknown) {
  const selector = `script[data-route="${path}"]`;
  const existing = document.head.querySelector<HTMLScriptElement>(selector);
  existing?.remove();

  const script = document.createElement("script");
  script.type = "application/ld+json";
  script.dataset.route = path;
  script.textContent = JSON.stringify(schema);
  document.head.append(script);
}

export function useRouteMetadata() {
  const { pathname } = useLocation();

  useEffect(() => {
    const route = findRoute(pathname);
    const routePath = route?.path ?? "/404";
    const metadata = route ?? findRoute("/404");

    if (!metadata) return;

    document.title = metadata.title;
    upsertMeta('meta[name="description"]', "name", "description", metadata.description);
    upsertMeta('meta[property="og:title"]', "property", "og:title", metadata.ogTitle);
    upsertMeta(
      'meta[property="og:description"]',
      "property",
      "og:description",
      metadata.ogDescription,
    );
    upsertMeta('meta[property="og:url"]', "property", "og:url", canonicalUrl(routePath));
    upsertMeta('meta[name="twitter:title"]', "name", "twitter:title", metadata.ogTitle);
    upsertMeta(
      'meta[name="twitter:description"]',
      "name",
      "twitter:description",
      metadata.ogDescription,
    );
    upsertCanonical(canonicalUrl(routePath));
    upsertSchema(routePath, metadata.schema);
  }, [pathname]);
}
